import * as Yup from 'yup';

import {
  getDefaultInputFieldsProps,
  mapSkillsToAutocompleteValues,
  FormFieldConfig,
  patchDataToSelect,
  shiftPatternDataToSelect,
  SkillOption,
  InputFieldProps,
  teamDataToSelect
} from 'components/Forms';
import Skill from 'models/Skills';
import Patch from 'models/Patches';
import ShiftPattern from 'models/ShiftPattern';
import { Filter, FilterFieldType } from 'components/FilterSearch/FilterSearch';

import {
  getRequiredErrorMessage,
  NUMBERS_ONLY_FIELD_ERROR_MESSAGE
} from 'constants/validation';
import { camelToSentenceCase } from 'utils';
import { skillDomain } from 'models';
import Team from 'models/Team';
import { getEfficiencyOptions } from './utils';
import { maxTravelOptions } from './EngineerSchedule/EditEngineerScheduleModal/maxTravelChangeFormConfig';

export enum EngineersFieldNames {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  PHONE_NUMBER = 'phoneNumber',
  EMAIL_ADDRESS = 'emailAddress',
  POSTCODE = 'postcode',
  TEAM = 'team',
  SKILLS = 'skills',
  COMPANY = 'company',
  MAX_TRAVEL_TIME = 'maxTravelTime',
  BYBOX = 'byBox',
  DEFAULT_PATCH = 'defaultPatch',
  DEFAULT_SHIFT_PATTERN = 'defaultShiftPattern',
  DEFAULT_EFFICIENCY = 'defaultEfficiency'
}

export function engineerFormFields(
  type: 'add' | 'edit',
  skills: Skill[] | null,
  patches: Patch[] | null,
  shiftPatterns: ShiftPattern[] | null,
  teams: Team[] | null
) {
  const fields: FormFieldConfig[] = [
    {
      required: true,
      name: 'firstName',
      autoFocus: true,
      qaId: `${type}EngineerFirstName`,
      inputType: 'textfield'
    },
    {
      required: true,
      name: 'lastName',
      qaId: `${type}EngineerLastName`,
      inputType: 'textfield'
    },
    {
      required: true,
      name: 'phoneNumber',
      qaId: `${type}EngineerPhoneNumber`,
      inputType: 'textfield'
    },
    {
      required: true,
      name: 'emailAddress',
      qaId: `${type}EngineerEmailAddress`,
      inputType: 'textfield'
    },
    {
      required: true,
      name: 'postcode',
      qaId: `${type}EngineerPostcode`,
      inputType: 'textfield'
    },
    {
      name: 'team',
      label: 'Team manager',
      qaId: `${type}EngineerTeam`,
      inputType: 'select',
      options:
        teams &&
        // Teams needs to be copied here as it's frozen (we are using strict mode for TS)
        [...teams]
          .sort((a, b) => a.managerName.localeCompare(b.managerName))
          .map(teamDataToSelect)
    },
    {
      required: true,
      name: 'skills',
      qaId: `${type}EngineerSkills`,
      inputType: 'multiselect',
      options: skills && mapSkillsToAutocompleteValues(skills),
      optionRenderer: SkillOption,
      preprocess: items => items && mapSkillsToAutocompleteValues(items)
    },
    {
      required: true,
      name: 'company',
      qaId: `${type}EngineerCompany`,
      inputType: 'textfield'
    },
    {
      name: 'workdayId',
      qaId: `${type}EngineerWorkdayId`,
      inputType: 'textfield'
    },
    {
      name: 'externalId',
      qaId: `${type}EngineerExternalId`,
      inputType: 'textfield'
    },
    {
      id: EngineersFieldNames.MAX_TRAVEL_TIME,
      label: 'Max travel',
      name: 'maxTravelTime',
      qaId: EngineersFieldNames.MAX_TRAVEL_TIME,
      inputType: 'select',
      options: maxTravelOptions
    } as InputFieldProps,
    {
      required: true,
      name: 'byBox',
      qaId: `${type}EngineerByBox`,
      inputType: 'textfield'
    },
    {
      required: true,
      name: 'defaultPatch',
      qaId: `${type}EngineerDefaultPatch`,
      inputType: 'select',
      options: patches && patches.map(patchDataToSelect)
    },
    {
      required: true,
      name: 'defaultShiftPattern',
      qaId: `${type}EngineerDefaultShiftPattern`,
      inputType: 'select',
      options: shiftPatterns && shiftPatterns.map(shiftPatternDataToSelect)
    },
    {
      required: true,
      name: 'defaultEfficiency',
      qaId: `${type}EngineerDefaultEfficiency`,
      inputType: 'select',
      options: getEfficiencyOptions()
    }
  ];

  return getDefaultInputFieldsProps(fields);
}

export const engineerSearchFields = getDefaultInputFieldsProps([
  {
    required: true,
    name: 'lastName',
    label: 'Engineer last name',
    qaId: `searchEngineer`,
    inputType: 'textfield'
  }
]);

export const validationSchema = () =>
  Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    phoneNumber: Yup.number().required('Required'),
    emailAddress: Yup.string().required('Required'),
    postcode: Yup.string().required('Required'),
    company: Yup.string().required('Required'),
    skills: Yup.array().required('Required'),
    maxTravelTime: Yup.string().required('Required'),
    byBox: Yup.string().required('Required'),
    defaultPatch: Yup.string().required('Required'),
    defaultShiftPattern: Yup.string().required('Required'),
    defaultEfficiency: Yup.string().required('Required')
  });

const emailValidationMessage = getRequiredErrorMessage(
  camelToSentenceCase(EngineersFieldNames.EMAIL_ADDRESS).toLowerCase()
);

export const engineersFormValidationSchema = () =>
  Yup.object().shape({
    [EngineersFieldNames.FIRST_NAME]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.FIRST_NAME).toLowerCase()
      )
    ),
    [EngineersFieldNames.LAST_NAME]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.LAST_NAME).toLowerCase()
      )
    ),
    [EngineersFieldNames.PHONE_NUMBER]: Yup.number()
      // This is needed because the default value for a field is an empty string,
      // therefore Yup will think it is a type error and display the wrong message
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value
      )
      .required(
        getRequiredErrorMessage(
          camelToSentenceCase(EngineersFieldNames.PHONE_NUMBER).toLowerCase()
        )
      )
      .typeError(NUMBERS_ONLY_FIELD_ERROR_MESSAGE),
    [EngineersFieldNames.EMAIL_ADDRESS]: Yup.string()
      .email(emailValidationMessage)
      .required(emailValidationMessage),
    [EngineersFieldNames.POSTCODE]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.POSTCODE).toLowerCase()
      )
    ),
    [EngineersFieldNames.TEAM]: Yup.string(),
    [EngineersFieldNames.SKILLS]: Yup.array().required(
      getRequiredErrorMessage(skillDomain.singular)
    ),
    [EngineersFieldNames.COMPANY]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.COMPANY).toLowerCase()
      )
    ),
    [EngineersFieldNames.MAX_TRAVEL_TIME]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.MAX_TRAVEL_TIME).toLowerCase()
      )
    ),
    [EngineersFieldNames.BYBOX]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.BYBOX).toLowerCase()
      )
    ),
    [EngineersFieldNames.DEFAULT_PATCH]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(EngineersFieldNames.DEFAULT_PATCH).toLowerCase()
      )
    ),
    [EngineersFieldNames.DEFAULT_SHIFT_PATTERN]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(
          EngineersFieldNames.DEFAULT_SHIFT_PATTERN
        ).toLowerCase()
      )
    ),
    [EngineersFieldNames.DEFAULT_EFFICIENCY]: Yup.string().required(
      getRequiredErrorMessage(
        camelToSentenceCase(
          EngineersFieldNames.DEFAULT_EFFICIENCY
        ).toLowerCase()
      )
    )
  });

export const engineerFilters: Filter[] = [
  {
    id: 'firstName',
    queryString: 'firstName',
    name: 'First Name',
    operator: '%',
    fieldType: FilterFieldType.TEXT
  },
  {
    id: 'lastName',
    queryString: 'lastName',
    name: 'Last Name',
    operator: '%',
    fieldType: FilterFieldType.TEXT
  },
  {
    id: 'postcode',
    queryString: 'postcode',
    name: 'Postcode',
    operator: '%',
    fieldType: FilterFieldType.TEXT
  }
];
